import {
  Button,
  Col,
  Flex,
  Form,
  Grid,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useAntTheme } from "hooks/useAntTheme";
import { CloseOutlined, DeleteOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { Cuepoint } from "../binder/types";
import { t } from "i18next";
import { useCuepointsStore } from "./useCuepointsStore";
import { useHighlightsStore } from "./useHighlightsStore";
import {
  ArrowCounterClockwiseIcon,
  CaretUpIcon,
  DotsThreeVerticalIcon,
} from "@phosphor-icons/react";
import Draggable from "react-draggable";
import styled from "styled-components";
import { PaletteResponse } from "pages/media/types";
import { STORAGE_SETTINGS_KEY } from "./constants";
import { postToIframe } from "./utils";
import {
  defaultBrackets,
  defaultRoughNotation,
} from "../animation/helpers/helpers";
import { useRemoveHighlight } from "./useRemoveHighlight";
import { ColorPicker } from "components/ColorPicker/ColorPicker";
import { radius } from "@libertify/ui";
const DEFAULT_COLOR = "#1677FF"; // blue

export const InspectorToolbar = ({
  handleNewCuepoint,
  brandKitPalette,
  projectId,
}: {
  handleNewCuepoint?: (update?: Partial<Cuepoint>) => void;
  brandKitPalette?: PaletteResponse;
  projectId: string;
}) => {
  const { theme } = useAntTheme();
  const { removeHighlight } = useRemoveHighlight(projectId);

  const {
    setCurrentHighlight,
    currentHighlight,
    setIsEditing,
    setHighlights,
    highlights,
  } = useHighlightsStore();

  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [latestColor, setLatestColor] = useState<string>(
    brandKitPalette?.highlight_color ?? DEFAULT_COLOR
  );
  const breakpoint = Grid.useBreakpoint();
  const isSmallScreen = !breakpoint.xl && !breakpoint.xxl;

  const {
    updateCount,
    currentCuepoint,
    setUpdateCount,
    updateCuepoint,
    setCurrentCuepoint,
  } = useCuepointsStore();

  const [updates, setUpdates] = useState(0);
  const [form] = Form.useForm();
  const { Option } = Select;

  //save current settings anywhere in case user want to revert.
  useEffect(() => {
    if (!currentCuepoint) return;

    localStorage.setItem(
      STORAGE_SETTINGS_KEY,
      JSON.stringify(currentCuepoint.roughNotation)
    );
  }, [currentCuepoint]);

  useEffect(() => {
    // when new highlight added, create new cuepoint
    if (!currentCuepoint) {
      handleNewCuepoint?.();
      if (currentHighlight) {
        setCurrentCuepoint(currentHighlight.id);
      }
    } else {
      form.setFieldsValue(currentCuepoint.roughNotation);
    }
  }, [
    currentCuepoint,
    currentHighlight,
    form,
    handleNewCuepoint,
    setCurrentCuepoint,
  ]);

  // when different highlight is selected without closing the toolbox, number of updates should be 0
  useEffect(() => {
    setUpdates(0);
  }, [currentHighlight]);

  const handleChange = (update: Partial<Cuepoint>, revert?: boolean) => {
    console.debug("updated properties", update);
    setUpdates((prev) => prev + 1);
    setHighlights([...highlights]);
    setIsEditing(true);
    setTimeout(() => {
      // hack to stop editing mode after scroll has been taken into account
      setIsEditing(false);
    }, 500);

    if (update.roughNotation) {
      if (update.roughNotation.color) {
        setLatestColor(update.roughNotation.color);
      }

      // if there is no cuepoint, create one with updated styles
      if (!currentCuepoint) {
        handleNewCuepoint?.(update);
        if (currentHighlight) {
          setCurrentCuepoint(currentHighlight.id);
        }
      } else {
        updateCuepoint(update.roughNotation, revert);
        setCurrentCuepoint(currentCuepoint.highlightId);
      }
    }
    postToIframe({
      type: "highlight-updated",
      id: currentHighlight.id,
      roughNotation: update.roughNotation,
    });
  };

  const handleClose = () => {
    setCurrentHighlight(undefined);
  };

  const handleRevert = () => {
    if (!updates) return;
    const updatesForSaving = updateCount - updates;
    setUpdateCount(updatesForSaving);

    const previousSettings = JSON.parse(
      localStorage.getItem(STORAGE_SETTINGS_KEY) ?? "{}"
    );

    handleChange(
      {
        roughNotation: previousSettings,
      },
      true
    );

    form.setFieldsValue(previousSettings);
    setUpdates(0);
  };

  const isBrackets = useMemo(() => {
    return currentCuepoint?.roughNotation?.type === "bracket";
  }, [currentCuepoint]);

  const bracketsTagRender = (props) => {
    const { label, onClose } = props;

    // Only show close button if there's more than one item
    const showClose = currentCuepoint?.roughNotation?.brackets?.length > 1;

    return (
      <Tag closable={showClose} onClose={onClose} style={{ marginRight: 2 }}>
        {label}
      </Tag>
    );
  };
  const ExpandIcon = showAdvancedSettings ? CaretUpIcon : DotsThreeVerticalIcon;

  return (
    <Draggable
      handle=".handle"
      bounds="parent"
      defaultPosition={{ x: -300, y: 0 }}
      scale={1}
      cancel=".ant-form-item, .ant-btn"
    >
      <Flex
        className="handle"
        gap={20}
        style={{
          width: "fit-content",
          position: "absolute",
          left: "50%",
          transform: "translate(-50%)",
          bottom: 40,
          zIndex: 5,
        }}
      >
        <Flex
          vertical={false}
          style={{
            backgroundColor: theme.colorBgBase,
            boxShadow: theme.boxShadowSecondary,
            border: `1px solid ${theme.colorBorder}`,
            borderRadius: radius.box,
            padding: 6,
            paddingBottom: 4,
            paddingTop: 0,
            width: 600,
          }}
          align="center"
          justify="space-between"
        >
          <Form
            initialValues={
              currentCuepoint?.roughNotation ?? defaultRoughNotation
            }
            form={form}
            colon={false}
          >
            <Flex vertical justify="center">
              <Row gutter={6} align="middle">
                <Col span={6}>
                  <Label type="secondary">
                    {t(`components.binder.BinderDrawerConfig.type`)}
                  </Label>
                  <Form.Item name="type" style={{ margin: 0 }}>
                    <Select
                      onChange={(value) => {
                        return handleChange({
                          roughNotation: {
                            type: value,
                            ...((value === "bracket" &&
                              // make sure to set brackets to left and right if no brackets are set
                              currentCuepoint?.roughNotation?.brackets
                                ?.length === 0 && {
                                brackets: defaultBrackets,
                              }) ??
                              {}),
                          },
                        });
                      }}
                    >
                      {[
                        "highlight",
                        "box",
                        "bracket",
                        "circle",
                        "underline",
                        "strike-through",
                        "crossed-off",
                      ].map((type) => (
                        <Option value={type} key={type}>
                          {type === "strike-through"
                            ? t(
                                `components.binder.BinderDrawerConfig.strikeThrough`
                              )
                            : type === "crossed-off"
                              ? t(
                                  `components.binder.BinderDrawerConfig.crossedOff`
                                )
                              : t(
                                  `components.binder.BinderDrawerConfig.${type}`
                                )}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={isBrackets ? 3 : 6}>
                  <Label type="secondary">
                    {t(`components.binder.BinderDrawerConfig.strokeWidth`)}
                  </Label>
                  <Form.Item name="strokeWidth" style={{ margin: 0 }}>
                    <InputNumber
                      min={1}
                      max={100}
                      onChange={(value) => {
                        handleChange({
                          roughNotation: {
                            type:
                              currentCuepoint?.roughNotation?.type ||
                              "highlight",
                            strokeWidth: value as number,
                          },
                        });
                      }}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col span={isBrackets ? 3 : 6}>
                  <Label type="secondary">
                    {t(`components.binder.BinderDrawerConfig.padding`)}
                  </Label>
                  <Form.Item name="padding" style={{ margin: 0 }}>
                    <InputNumber
                      placeholder={t(
                        "components.binder.BinderDrawerConfig.pleaseEnterAPadding"
                      )}
                      min={0}
                      max={100}
                      onChange={(value) =>
                        handleChange({
                          roughNotation: {
                            type:
                              currentCuepoint?.roughNotation?.type ||
                              "underline",
                            padding: value as number,
                          },
                        })
                      }
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                {isBrackets && (
                  <Col span={7}>
                    <Label type="secondary">
                      {t(`components.binder.BinderDrawerConfig.brackets`)}
                    </Label>
                    <Form.Item name="brackets" style={{ margin: 0 }}>
                      <Select
                        mode="multiple"
                        showSearch={false}
                        placement="topLeft"
                        maxTagCount="responsive"
                        tagRender={bracketsTagRender}
                        onChange={(value) => {
                          handleChange({
                            roughNotation: {
                              type:
                                currentCuepoint?.roughNotation?.type ||
                                "underline",
                              brackets: value,
                            },
                          });
                        }}
                        style={{ height: 32 }}
                      >
                        {["left", "right", "top", "bottom"].map((side) => {
                          return (
                            <Option value={side} key={side}>
                              {t(
                                `components.binder.BinderDrawerConfig.${side}`
                              )}
                            </Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
                <Col span={5}>
                  <Label type="secondary">
                    {t(`components.binder.BinderDrawerConfig.color`)}
                  </Label>
                  <Form.Item
                    name="color"
                    style={{ margin: 0, display: "flex", alignItems: "start" }}
                  >
                    <ColorPicker
                      showText
                      value={
                        currentCuepoint?.roughNotation?.color || latestColor
                      }
                      palette={brandKitPalette}
                      onChangeComplete={(value) => {
                        handleChange({
                          roughNotation: {
                            type:
                              currentCuepoint?.roughNotation?.type ||
                              "underline",
                            color: value.toHexString(),
                          },
                        });
                      }}
                      showBorders={false}
                    />
                  </Form.Item>
                </Col>
              </Row>
              {showAdvancedSettings && (
                <Row gutter={16} align="middle">
                  <Col span={6}>
                    <Label type="secondary">
                      {t(`components.binder.BinderDrawerConfig.animation`)}
                    </Label>
                    <Form.Item name="animate">
                      <Select
                        placeholder={t(
                          "components.binder.BinderDrawerConfig.pleaseSelect"
                        )}
                        onChange={(value) =>
                          handleChange({
                            roughNotation: {
                              type:
                                currentCuepoint?.roughNotation?.type ||
                                "underline",
                              animate: value,
                            },
                          })
                        }
                      >
                        <Option value={true}>
                          {t("components.binder.BinderDrawerConfig.animated")}
                        </Option>
                        <Option value={false}>
                          {t(
                            "components.binder.BinderDrawerConfig.notAnimated"
                          )}
                        </Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Label type="secondary">
                      {t(
                        `components.binder.BinderDrawerConfig.animationDuration`
                      )}
                    </Label>
                    <Form.Item name="animationDuration">
                      <InputNumber
                        min={0}
                        max={3000}
                        placeholder={t(
                          "components.binder.BinderDrawerConfig.pleaseEnterADuration"
                        )}
                        step={100}
                        onChange={(value) =>
                          handleChange({
                            roughNotation: {
                              type:
                                currentCuepoint?.roughNotation?.type ||
                                "underline",
                              animationDuration: value as number,
                            },
                          })
                        }
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Label type="secondary">
                      {t(`components.binder.BinderDrawerConfig.iterations`)}
                    </Label>
                    <Form.Item name="iterations">
                      <InputNumber
                        placeholder={t(
                          "components.binder.BinderDrawerConfig.pleaseEnterAn"
                        )}
                        onChange={(value) =>
                          handleChange({
                            roughNotation: {
                              type:
                                currentCuepoint?.roughNotation?.type ||
                                "underline",
                              iterations: value as number,
                            },
                          })
                        }
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Label type="secondary">
                      {t(`components.binder.BinderDrawerConfig.delay`)}
                    </Label>
                    <Form.Item name="animationDelay">
                      <InputNumber
                        placeholder={t(
                          "components.binder.BinderDrawerConfig.pleaseEnterADelay"
                        )}
                        step={100}
                        onChange={(value) =>
                          handleChange({
                            roughNotation: {
                              type:
                                currentCuepoint?.roughNotation?.type ||
                                "underline",
                              animationDelay: value as number,
                            },
                          })
                        }
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={5}>
                    <Label type="secondary">
                      {t(`components.binder.BinderDrawerConfig.multiline`)}
                    </Label>
                    <Form.Item name="multiline" style={{ width: "100%" }}>
                      <Select
                        // placeholder={t(
                        //   "components.binder.BinderDrawerConfig.pleaseSelect"
                        // )}
                        onChange={(value) =>
                          handleChange({
                            roughNotation: {
                              type:
                                currentCuepoint?.roughNotation?.type ||
                                "underline",
                              multiline: value,
                            },
                          })
                        }
                      >
                        <Option value={true}>
                          {t("components.binder.BinderDrawerConfig.multiline")}
                        </Option>
                        <Option value={false}>
                          {t(
                            "components.binder.BinderDrawerConfig.notMultiline"
                          )}
                        </Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Flex>
          </Form>
          <Button
            type="text"
            size="large"
            icon={<ExpandIcon weight="bold" />}
            style={{
              cursor: "pointer",
              marginRight: 4,
            }}
            onClick={() => setShowAdvancedSettings((value) => !value)}
          />
        </Flex>
        <Flex
          align="center"
          // gap={2}
          style={{
            backgroundColor: theme.colorBgBase,
            boxShadow: theme.boxShadowSecondary,
            border: `1px solid ${theme.colorBorder}`,
            borderRadius: radius.box,
            paddingRight: 7,
            paddingLeft: 7,
            height: 60,
            position: "absolute",
            top: isSmallScreen ? -65 : 0,
            right: isSmallScreen ? 0 : -150,
          }}
        >
          <Tooltip
            title={t("components.binder.InspectorToolbar.deleteAnimation")}
          >
            <Popconfirm
              title="Are you sure?"
              onConfirm={() => {
                removeHighlight(currentHighlight);
              }}
            >
              <Button
                type="text"
                size="large"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>

          <Tooltip
            title={t("components.binder.InspectorToolbar.revertChanges")}
          >
            <Flex style={{ backgroundColor: theme.colorBgBase }}>
              <Button
                disabled={updateCount === 0}
                type="text"
                size="large"
                icon={<ArrowCounterClockwiseIcon weight="bold" />}
                onClick={handleRevert}
              />
            </Flex>
          </Tooltip>
          <Tooltip
            title={t("components.binder.InspectorToolbar.closeSettings")}
          >
            <Button
              type="text"
              size="large"
              onClick={handleClose}
              icon={<CloseOutlined />}
            />
          </Tooltip>
        </Flex>
      </Flex>
    </Draggable>
  );
};

const Label = styled(Typography.Text)`
  font-size: 10px;
  font-weight: bold;
`;
